import { useEffect, useState } from 'react';
import { Dialog } from 'primereact/dialog';
import styled from 'styled-components';
import VerifierCompaniesList from 'components/v2/compiler/panels/third-party-verification/epd-verifier-selector/VerifierCompaniesList';
import CompilerSelectControlledComponent from 'components/v2/epd-compiler-inputs/CompilerSelectControlledComponent';
import WizardTextInputControlledComponent from 'components/v2/epd-wizard-inputs/WizardTextInputControlled';
import { HelpBox } from 'components/v2/help-boxes';
import { toaster } from 'components/v2/toast';
import { GLOBAL_OPTION } from 'constants/constants';
import { useVerifierMarketplaceCountriesLookupWithGlobal } from 'hooks/lookups';
import CompanyService from 'services/CompanyService';
import { VerificationType } from 'services/EpdClient';
import { AddVerifierToCompanyModel } from 'types/dynamic-forms/companies/AddVerifierToCompanyModel';
import { EPDVerifierCompanyModel, Option, VerifierType } from 'types/types';

const VerifierFilterOption = {
  All: 'All',
  ApprovedIndividual: 'Approved Individual Verifiers',
  CertificationBodies: 'Accredited Certification Bodies',
};

type TProps = {
  onHide(): void;
  isOpened: boolean;
  companyId: string;
  onAddVerifier(model: AddVerifierToCompanyModel): Promise<void>;
};

const VerifierSelectInputRendererDialog = ({ onHide, isOpened, companyId, onAddVerifier }: TProps) => {
  const [verifiers, setVerifiers] = useState<EPDVerifierCompanyModel[]>([]);
  const [filteredVerifierCompanies, setFilteredVerifierCompanies] = useState<EPDVerifierCompanyModel[]>([]);
  const [selectedCountry, setSelectedCountry] = useState<Option | undefined>();
  const [selectedVerifier, setSelectedVerifier] = useState<EPDVerifierCompanyModel | undefined>();
  const verifierTypeOptions = [
    { value: VerifierFilterOption.All, label: VerifierFilterOption.All },
    { value: VerifierFilterOption.ApprovedIndividual, label: VerifierFilterOption.ApprovedIndividual },
    { value: VerifierFilterOption.CertificationBodies, label: VerifierFilterOption.CertificationBodies },
  ] as Option[];
  const [areButtonsDisabled, setAreButtonsDisabled] = useState<boolean>(false);

  const [selectedVerifierType, setSelectedVerifierType] = useState<Option>(verifierTypeOptions[0]);

  const countries = useVerifierMarketplaceCountriesLookupWithGlobal().data || [];

  const onChangeVerifierCountry = async (propertyName: string, val: any) => {
    if (!countries.length) return;
    setSelectedCountry(countries.find((c) => c.value === val));
  };

  const onChangeVerifierType = async (propertyName: string, val: any) => {
    setSelectedVerifierType(verifierTypeOptions.find((v) => v.value === val)!);
  };

  const handleAddVerifier = async (verifierId: string) => {
    setAreButtonsDisabled(true);

    try {
      const verifier = verifiers.find((v) => v.id === verifierId);
      if (!verifier) {
        throw new Error('Verifier is selected but not found in data set');
      }

      const connectVerifierModel: AddVerifierToCompanyModel = {
        certificationBodyCompanyId: verifier.verifierType === VerifierType.Body ? verifierId : null,
        individualVerifierMembershipId: verifier.verifierType === VerifierType.Individual ? verifierId : null,
      };
      await onAddVerifier(connectVerifierModel);
      toaster({
        severity: 'success',
        summary: 'The verifier was added to the EPD!',
        details: 'Now the EPD Verifier has an access to the EPD.',
      });
      onHide();
    } catch {
      setFilteredVerifierCompanies((prev) =>
        prev.map((c) => (c.id === selectedVerifier?.id ? { ...c, errorText: 'Something went wrong.' } : c))
      );
    } finally {
      setAreButtonsDisabled(false);
    }
  };

  useEffect(() => {
    const fetchCompanyVerifiers = async () => {
      let result: EPDVerifierCompanyModel[] = [];

      switch (selectedVerifierType.value) {
        case VerifierFilterOption.All:
          result = await CompanyService.getCompanyVerifiers(
            companyId,
            selectedCountry?.value,
            selectedCountry?.label === GLOBAL_OPTION.label ? true : undefined,
            undefined,
            VerificationType.EPDVerification
          );
          break;
        case VerifierFilterOption.ApprovedIndividual:
          result = await CompanyService.getCompanyIndividualVerifiers(
            companyId,
            selectedCountry?.value,
            selectedCountry?.label === GLOBAL_OPTION.label ? true : undefined,
            undefined,
            VerificationType.EPDVerification
          );
          break;
        case VerifierFilterOption.CertificationBodies:
          result = await CompanyService.getCompanyCertificationBodies(
            companyId,
            selectedCountry?.value,
            selectedCountry?.label === GLOBAL_OPTION.label ? true : undefined,
            undefined,
            VerificationType.EPDVerification
          );
          break;
      }

      setVerifiers(result);
      setFilteredVerifierCompanies(result);
    };

    fetchCompanyVerifiers();
  }, [companyId, selectedCountry, selectedVerifierType, isOpened]);

  const handleSearchVerifierCompanies = (event: any) => {
    const searchTerm = event.target.value.toLowerCase();
    if (!searchTerm) {
      setFilteredVerifierCompanies(verifiers);
      return;
    }
    setFilteredVerifierCompanies((prev) =>
      prev.filter((item) => item.name?.toLowerCase().includes(searchTerm) || item.email?.toLowerCase().includes(searchTerm))
    );
  };

  return (
    <StyledDialog
      resizable={false}
      header="EPD Verifier"
      visible={isOpened}
      contentStyle={!!filteredVerifierCompanies.length ? {} : { overflowY: 'initial' }}
      onHide={() => {
        setSelectedVerifier(undefined);
        setSelectedCountry(undefined);
        setSelectedVerifierType(verifierTypeOptions[0]);
        onHide();
      }}
    >
      <DialogContentContainer>
        <HelpBox>
          To get in touch with one or several verifiers please click on the button “Request collaboration”. An e-mail will be
          sent to the verifier and they will reach out to respond to your query. Once you will get the verifier’s
          collaboration response, you can “Add verifier” to your EPD.
        </HelpBox>
        <DialogContentRow>
          <CompilerSelectControlledComponent
            label="Country"
            name="verifierCountry"
            options={countries}
            value={selectedCountry}
            onChanged={onChangeVerifierCountry}
            placeholder="Select"
            isClearable={true}
          />
        </DialogContentRow>
        <DialogContentRow>
          <CompilerSelectControlledComponent
            label="Verifier type"
            name="verifierType"
            options={verifierTypeOptions}
            value={selectedVerifierType}
            onChanged={onChangeVerifierType}
            placeholder="Select"
            isClearable={false}
          />
        </DialogContentRow>
        <DialogContentRow>
          <WizardTextInputControlledComponent
            label="Verifier name or email"
            tooltipPosition={'right'}
            value={undefined}
            placeholder="Type here to search"
            onChange={handleSearchVerifierCompanies}
            onChanged={() => {}}
            name="nameOrEmail"
            maxLength={150}
          />
        </DialogContentRow>
        <VerifierCompaniesList
          epdVerifier={null}
          verifierCompanies={filteredVerifierCompanies}
          selectedVerifierCompany={selectedVerifier}
          onSelectVerifierCompany={setSelectedVerifier}
          onAddVerifier={handleAddVerifier}
          buttonsAreDisabled={areButtonsDisabled}
        />
      </DialogContentContainer>
    </StyledDialog>
  );
};

const StyledDialog = styled(Dialog)`
  width: 35rem;

  .p-dialog-header {
    position: relative;
    z-index: 1;
    box-shadow: 0px 4px 4px 0px rgba(0, 0, 0, 0.04);
  }

  .p-dialog-header-close svg path {
    fill: ${(props) => props.theme.colors.primaryBlack};
  }
`;

const DialogContentContainer = styled.div`
  padding: 0.25rem 2rem 2rem 2rem;
  scrollbar-gutter: stable;
`;

const DialogContentRow = styled.div`
  margin-top: 25px;
  margin-bottom: 8px;
`;

export default VerifierSelectInputRendererDialog;
